import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays,
  Check,
  ChevronDown,
  FileText,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Upload,
  X,
} from "lucide-react";
import { API, api, apiForm, formatApiError } from "@/lib/api";
import {
  KESIMPULAN_OPTIONS,
  daysBetween,
  formatTanggal,
  pickFileGuard,
} from "./shared";

const EMPTY = {
  perihal: "",
  tgl_diterima: "",
  tgl_dijawab: "",
  kesimpulan: "",
  pengelola: "",
};

export default function AdminView({ user }) {
  const [form, setForm] = useState(EMPTY);
  const [filePermohonan, setFilePermohonan] = useState(null);
  const [fileJawaban, setFileJawaban] = useState(null);
  const [editing, setEditing] = useState(null);
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openStatus, setOpenStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, acc] = await Promise.all([
        api("/api/permohonan"),
        api("/api/admin/accounts"),
      ]);
      setEntries(d.entries || []);
      setAccounts((acc || []).filter((a) => a.key !== "ppid" && a.key !== "pts"));
    } catch (e) {
      toast(formatApiError(e.message));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY);
    setFilePermohonan(null);
    setFileJawaban(null);
  };

  const startEdit = (e) => {
    setEditing(e);
    setForm({
      perihal: e.perihal || "",
      tgl_diterima: e.tgl_diterima || "",
      tgl_dijawab: e.tgl_dijawab || "",
      kesimpulan: e.kesimpulan || "",
      pengelola: e.pengelola || "",
    });
    setFilePermohonan(null);
    setFileJawaban(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("perihal", form.perihal);
      fd.append("tgl_diterima", form.tgl_diterima);
      fd.append("tgl_dijawab", form.tgl_dijawab);
      fd.append("kesimpulan", form.kesimpulan);
      fd.append("pengelola", form.pengelola);
      if (filePermohonan) fd.append("dokumen_permohonan", filePermohonan);
      if (fileJawaban) fd.append("dokumen_jawaban", fileJawaban);
      if (editing) await apiForm(`/api/permohonan/${editing.id}`, fd);
      else await apiForm("/api/permohonan", fd);
      toast.success(editing ? "Permohonan diperbarui" : "Permohonan tersimpan");
      cancelEdit();
      await load();
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    setBusy(true);
    try {
      await api(`/api/permohonan/${e.id}`, { method: "DELETE" });
      if (editing?.id === e.id) cancelEdit();
      toast.success("Permohonan dihapus");
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (e, status) => {
    setBusy(true);
    setOpenStatus(null);
    try {
      await api(`/api/permohonan/${e.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      toast.success(
        status === "final"
          ? "Permohonan disetujui — status FINAL"
          : "Permohonan dikembalikan ke DRAF"
      );
      await load();
    } catch (err) {
      toast(formatApiError(err.message));
    } finally {
      setBusy(false);
    }
  };

  const hari = daysBetween(form.tgl_diterima, form.tgl_dijawab);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
          Permohonan Informasi Publik
        </h1>
        <span className="rounded-full bg-[#1479BF]/10 px-3 py-1 text-xs font-bold text-[#1479BF]">
          Tahun 2026
        </span>
      </div>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
        Input daftar permohonan informasi publik yang diterima LLDIKTI Wilayah
        VII beserta jawabannya. Hitungan hari terjawab dihitung otomatis sejak
        tanggal permohonan diterima.
      </p>

      <section
        data-testid="permohonan-form"
        className="mt-8 rounded-3xl border border-[#E3EBF3] bg-white p-6"
      >
        <h2 className="font-display text-lg font-semibold text-[#0B2440]">
          {editing ? "Edit Permohonan" : "Input Daftar Permohonan Publik"}
        </h2>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              1. Unggah Dokumen Permohonan
            </span>
            <label
              data-testid="permohonan-file-dokumen"
              className="mt-1.5 inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg border border-[#E3EBF3] bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-[#1479BF] hover:text-[#1479BF]"
            >
              <Upload aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {filePermohonan
                  ? filePermohonan.name
                  : "Pilih berkas (PDF/Word/Excel · maks. 25MB)"}
              </span>
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) =>
                  pickFileGuard(
                    e.target.files[0] || null,
                    setFilePermohonan
                  )
                }
              />
            </label>
          </div>

          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              2. Perihal Dokumen
            </span>
            <input
              data-testid="permohonan-perihal"
              value={form.perihal}
              onChange={(e) => set("perihal", e.target.value)}
              placeholder="Perihal permohonan"
              className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
            />
          </div>

          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              3. Tanggal Permohonan Diterima LLDIKTI VII
            </span>
            <input
              type="date"
              data-testid="permohonan-tgl-diterima"
              value={form.tgl_diterima}
              onChange={(e) => set("tgl_diterima", e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
            />
          </div>

          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              4. Tanggal Permohonan Dijawab
            </span>
            <input
              type="date"
              data-testid="permohonan-tgl-dijawab"
              value={form.tgl_dijawab}
              onChange={(e) => set("tgl_dijawab", e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
            />
          </div>

          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              5. Unggah Dokumen Jawaban
            </span>
            <label
              data-testid="permohonan-file-jawaban"
              className="mt-1.5 inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg border border-[#E3EBF3] bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-colors duration-200 hover:border-[#1479BF] hover:text-[#1479BF]"
            >
              <Upload aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {fileJawaban ? fileJawaban.name : "Pilih berkas (PDF/Word/Excel · maks. 25MB)"}
              </span>
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) =>
                  pickFileGuard(e.target.files[0] || null, setFileJawaban)
                }
              />
            </label>
          </div>

          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              6. Kesimpulan Jawaban
            </span>
            <select
              data-testid="permohonan-kesimpulan"
              value={form.kesimpulan}
              onChange={(e) => set("kesimpulan", e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] bg-white px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
            >
              <option value="">— Pilih kesimpulan —</option>
              {KESIMPULAN_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
              7. Pengelola Dokumen
            </span>
            <select
              data-testid="permohonan-pengelola"
              value={form.pengelola}
              onChange={(e) => set("pengelola", e.target.value)}
              className="mt-1.5 w-full rounded-lg border border-[#E3EBF3] bg-white px-3 py-2 text-sm outline-none transition focus:border-[#1479BF]"
            >
              <option value="">— Pilih pengelola —</option>
              {accounts.map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {hari != null && (
          <p
            data-testid="permohonan-form-hari"
            className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
              hari >= 0
                ? "bg-[#EFF6FC] text-[#1479BF]"
                : "bg-red-50 text-red-500"
            }`}
          >
            <CalendarDays aria-hidden="true" className="h-3.5 w-3.5" />
            {hari >= 0
              ? `Terjawab dalam ${hari} hari`
              : "Tanggal dijawab mendahului tanggal diterima"}
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            data-testid="permohonan-save"
            onClick={save}
            disabled={busy || !form.perihal.trim() || !form.pengelola}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1479BF] px-5 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-[#0B2440] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {editing ? (
              <Save aria-hidden="true" className="h-3.5 w-3.5" />
            ) : (
              <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            )}
            {editing ? "Simpan Perubahan" : "Simpan Permohonan"}
          </button>
          {editing && (
            <button
              data-testid="permohonan-cancel"
              onClick={cancelEdit}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#E3EBF3] px-5 py-2 text-xs font-bold text-slate-600 transition-colors duration-200 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
            >
              <X aria-hidden="true" className="h-3.5 w-3.5" />
              Batal
            </button>
          )}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-[#0B2440]">
          Daftar Permohonan
        </h2>
        {entries.length === 0 ? (
          <p
            data-testid="permohonan-empty"
            className="mt-4 rounded-2xl border border-dashed border-[#E3EBF3] bg-white px-6 py-10 text-center text-sm text-slate-400"
          >
            Belum ada permohonan yang diinput.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            {entries.map((e, i) => {
              const h = daysBetween(e.tgl_diterima, e.tgl_dijawab);
              return (
                <div
                  key={e.id}
                  data-testid={`permohonan-entry-${i + 1}`}
                  className="rounded-2xl border border-[#E3EBF3] bg-white p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <p className="text-sm font-semibold leading-snug text-[#0B2440]">
                      {i + 1}. {e.perihal || "—"}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <button
                          data-testid={`permohonan-status-${i + 1}`}
                          onClick={() =>
                            setOpenStatus(openStatus === e.id ? null : e.id)
                          }
                          disabled={busy}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.08em] transition-colors duration-200 disabled:opacity-50 ${
                            e.status === "final"
                              ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              : "bg-amber-50 text-amber-600 hover:bg-amber-100"
                          }`}
                        >
                          {e.status === "final" ? "FINAL" : "DRAF"}
                          <ChevronDown
                            aria-hidden="true"
                            className={`h-3 w-3 transition-transform duration-200 ${
                              openStatus === e.id ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {openStatus === e.id && (
                          <div
                            data-testid={`permohonan-status-menu-${i + 1}`}
                            className="absolute right-0 z-20 mt-1.5 w-48 overflow-hidden rounded-xl border border-[#E3EBF3] bg-white shadow-lg"
                          >
                            {e.status === "final" ? (
                              <button
                                data-testid={`permohonan-status-to-draf-${i + 1}`}
                                onClick={() => setStatus(e, "draf")}
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold text-amber-600 transition-colors duration-150 hover:bg-amber-50"
                              >
                                <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
                                Kembalikan ke Draft
                              </button>
                            ) : (
                              <button
                                data-testid={`permohonan-status-to-final-${i + 1}`}
                                onClick={() => setStatus(e, "final")}
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold text-emerald-600 transition-colors duration-150 hover:bg-emerald-50"
                              >
                                <Check aria-hidden="true" className="h-3.5 w-3.5" />
                                Setujui
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        data-testid={`permohonan-edit-${i + 1}`}
                        onClick={() => startEdit(e)}
                        title="Edit permohonan"
                        className="inline-flex items-center gap-1 rounded-full border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-600 transition-colors duration-200 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                      >
                        <Pencil aria-hidden="true" className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        data-testid={`permohonan-delete-${i + 1}`}
                        onClick={() => remove(e)}
                        title="Hapus permohonan"
                        className="rounded-full border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-400 transition-colors duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                    <p>
                      <span className="font-semibold text-slate-400">
                        Pengelola:{" "}
                      </span>
                      {e.pengelola_label || "—"}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-400">
                        Diterima:{" "}
                      </span>
                      {formatTanggal(e.tgl_diterima)}
                    </p>
                    <p>
                      <span className="font-semibold text-slate-400">
                        Dijawab:{" "}
                      </span>
                      {formatTanggal(e.tgl_dijawab)}
                    </p>
                    <p>
                      {h != null && h >= 0 ? (
                        <span
                          data-testid={`permohonan-hari-${i + 1}`}
                          className="inline-flex items-center gap-1 font-bold text-[#1479BF]"
                        >
                          <CalendarDays aria-hidden="true" className="h-3.5 w-3.5" />
                          Terjawab dalam {h} hari
                        </span>
                      ) : (
                        <span className="text-slate-300">Belum terjawab</span>
                      )}
                    </p>
                  </div>
                  {e.kesimpulan && (
                    <p
                      className="mt-2 max-w-3xl truncate text-xs font-semibold text-[#0B2440]"
                      title={e.kesimpulan}
                    >
                      <span className="font-semibold text-slate-400">
                        Kesimpulan:{" "}
                      </span>
                      {e.kesimpulan}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-3">
                    {e.dokumen_permohonan && (
                      <a
                        data-testid={`permohonan-doc-permohonan-${i + 1}`}
                        href={`${API}/api/permohonan/file/${e.dokumen_permohonan.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1479BF] underline"
                      >
                        <FileText aria-hidden="true" className="h-4 w-4" />
                        {e.dokumen_permohonan.name}
                      </a>
                    )}
                    {e.dokumen_jawaban && (
                      <a
                        data-testid={`permohonan-doc-jawaban-${i + 1}`}
                        href={`${API}/api/permohonan/file/${e.dokumen_jawaban.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1479BF] underline"
                      >
                        <FileText aria-hidden="true" className="h-4 w-4" />
                        {e.dokumen_jawaban.name}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}